import GlobalStyles from '@mui/material/GlobalStyles';
import {
  chartsTooltipClasses,
  ChartsTooltipContainer,
  type ChartsTooltipProps,
  useAxesTooltip,
} from '@mui/x-charts/ChartsTooltip';
import { useAnimateBar } from '@mui/x-charts/hooks/animation';
import {
  memo,
  type MouseEvent as ReactMouseEvent,
  type Ref,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { tintOnWhite } from '@/theme/colorUtils';
import { r, radii } from '../../theme/tokens';
import { getUiTokens, type UiTone } from '../../theme/uiTokens';
import { chartBarSizing } from '../theme/chartBarSizing';
import { Box } from './Box';
import { formatChartDateLabel } from './chartDateLabel';
import { buildTickLabelInterval } from './chartTickLabelInterval';
import { darken } from './colorUtils';
import { useTheme } from './hooks';
import { MuiBarChart } from './xCharts';
import type { BarProps } from '@mui/x-charts/BarChart';
import type { BarItemIdentifier, ChartsAxisData } from '@mui/x-charts/models';

export type StackedBarChartDatum = Record<string, string | number>;

type StackedBarSeries = { key: string; label: string; color?: string; colorKey?: UiTone };

type StackedBarChartProps = {
  data: StackedBarChartDatum[];
  series: StackedBarSeries[];
  height?: number;
  stackId?: string;
  skipAnimation?: boolean;
  onSelectBar?: (dataIndex: number, seriesKey?: string) => void;
  disableInteraction?: boolean;
  disableTooltip?: boolean;
};

type StackedTotalsTooltipProps = ChartsTooltipProps & {
  totalsByLabel: ReadonlyMap<string, number>;
};

const StackedTotalsTooltip = ({
  totalsByLabel,
  classes,
  sx,
  ...props
}: StackedTotalsTooltipProps): JSX.Element | null => {
  const tooltipData = useAxesTooltip();
  if (!tooltipData) return null;
  const hasAnyBreakdownRows = tooltipData.some(({ axisFormattedValue, seriesItems }) => {
    const derivedTotal = seriesItems.reduce((sum, item) => {
      const value = typeof item.value === 'number' ? item.value : Number(item.value);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0);
    const total = derivedTotal > 0 ? derivedTotal : (totalsByLabel.get(axisFormattedValue) ?? 0);
    const hasVisibleSeries = seriesItems.some(({ formattedValue }) => Boolean(formattedValue));
    return total > 0 || hasVisibleSeries;
  });
  const paperClassName = `${classes?.paper ?? ''} ${
    hasAnyBreakdownRows ? '' : 'flowqa-stacked-tooltip-paper-empty'
  }`.trim();
  const tableClassName = `${chartsTooltipClasses.table} ${classes?.table ?? ''}`.trim();
  const rowClassName = chartsTooltipClasses.row;
  const baseCellClassName = `${chartsTooltipClasses.cell} ${classes?.cell ?? ''}`.trim();
  const labelCellClassName = `${baseCellClassName} ${chartsTooltipClasses.labelCell}`.trim();
  const valueCellClassName = `${baseCellClassName} ${chartsTooltipClasses.valueCell}`.trim();
  const markContainerClassName = chartsTooltipClasses.markContainer;
  const markClassName = `${chartsTooltipClasses.mark} ${classes?.mark ?? ''}`.trim();

  return (
    <ChartsTooltipContainer classes={classes} sx={sx} {...props}>
      <div className={paperClassName}>
        {tooltipData.map(({ axisId, axisFormattedValue, seriesItems }) => {
          const derivedTotal = seriesItems.reduce((sum, item) => {
            const value = typeof item.value === 'number' ? item.value : Number(item.value);
            return Number.isFinite(value) ? sum + value : sum;
          }, 0);
          const total =
            derivedTotal > 0 ? derivedTotal : (totalsByLabel.get(axisFormattedValue) ?? 0);
          const showTotal = total > 0;
          const hasVisibleBreakdownRows =
            showTotal || seriesItems.some(({ formattedValue }) => Boolean(formattedValue));
          return (
            <table
              key={String(axisId)}
              className={`${tableClassName} ${
                hasVisibleBreakdownRows ? '' : 'flowqa-stacked-tooltip-table-empty'
              }`.trim()}
            >
              <thead>
                <tr className="flowqa-stacked-tooltip-header-row">
                  <th className={labelCellClassName}>{axisFormattedValue}</th>
                  {showTotal ? (
                    <td
                      className={`${valueCellClassName} flowqa-stacked-tooltip-header-total-label`}
                      colSpan={2}
                    >
                      {`Total: ${total}`}
                    </td>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {seriesItems.map(({ seriesId, color, formattedLabel, formattedValue }) => {
                  if (!formattedValue) return null;
                  const numericValue = Number(formattedValue);
                  const percentage =
                    total > 0 && Number.isFinite(numericValue)
                      ? `(${Math.round((numericValue / total) * 100)}%)`
                      : null;
                  return (
                    <tr key={String(seriesId)} className={rowClassName}>
                      <th className={labelCellClassName}>
                        <div className={markContainerClassName}>
                          <span
                            className={markClassName}
                            style={{
                              display: 'inline-block',
                              width: 8,
                              height: 8,
                              borderRadius: 2,
                              backgroundColor: color,
                            }}
                          />
                        </div>
                        {formattedLabel ?? ''}
                      </th>
                      <td className={`${valueCellClassName} flowqa-stacked-tooltip-count-cell`}>
                        {formattedValue}
                      </td>
                      <td className={`${valueCellClassName} flowqa-stacked-tooltip-percent-cell`}>
                        {percentage}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          );
        })}
      </div>
    </ChartsTooltipContainer>
  );
};

const StackedBarChartBase = ({
  data,
  series,
  height = 260,
  stackId = 'total',
  skipAnimation = false,
  onSelectBar,
  disableInteraction = false,
  disableTooltip = false,
}: StackedBarChartProps): JSX.Element => {
  const categoryGapRatio = chartBarSizing.default.categoryGapRatio;
  const barGapRatio = chartBarSizing.default.barGapRatio;
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const resizeResetTimerRef = useRef<number | null>(null);
  const [suspendAnimationForResize, setSuspendAnimationForResize] = useState(false);
  const tokens = getUiTokens(theme.palette.mode === 'dark' ? 'dark' : 'light');
  const barRadius = radii.md;
  const lastItemClickRef = useRef<{ dataIndex: number; timestamp: number } | null>(null);
  const lastItemClickTimeoutRef = useRef<number | null>(null);
  const handleItemClick = useCallback(
    (_event: ReactMouseEvent<SVGElement>, item: BarItemIdentifier) => {
      const dataIndex = item.dataIndex;
      if (typeof dataIndex !== 'number' || !Number.isFinite(dataIndex)) return;
      const seriesKey = item.seriesId ? String(item.seriesId) : undefined;
      lastItemClickRef.current = { dataIndex, timestamp: Date.now() };
      if (lastItemClickTimeoutRef.current !== null) {
        window.clearTimeout(lastItemClickTimeoutRef.current);
      }
      lastItemClickTimeoutRef.current = window.setTimeout(() => {
        lastItemClickRef.current = null;
        lastItemClickTimeoutRef.current = null;
      }, 600);
      onSelectBar?.(dataIndex, seriesKey);
    },
    [onSelectBar],
  );
  const handleAxisClick = useCallback(
    (_event: MouseEvent, axisData: ChartsAxisData | null) => {
      const target = _event.target;
      if (target && target instanceof Element && target.closest('.MuiBarElement-root')) {
        return;
      }
      const dataIndex = axisData?.dataIndex;
      if (typeof dataIndex !== 'number' || !Number.isFinite(dataIndex)) return;
      const lastItemClick = lastItemClickRef.current;
      if (lastItemClick && Date.now() - lastItemClick.timestamp < 600) {
        return;
      }
      onSelectBar?.(dataIndex);
    },
    [onSelectBar],
  );
  const formatWholeNumber = (value: number | null): string => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '';
    return Number.isInteger(value) ? String(value) : '';
  };
  const formatTooltipValue = (value: number | null): string | null => {
    if (typeof value !== 'number' || !Number.isFinite(value) || value === 0) return null;
    return formatWholeNumber(value);
  };
  const palette = [
    tokens.statusColors.purple,
    tokens.statusColors.green,
    tokens.statusColors.orange,
    tokens.statusColors.blue,
    tokens.statusColors.red,
  ];
  const resolveBarColor = (base: string): string =>
    theme.palette.mode === 'dark' ? darken(base, 0.26) : tintOnWhite(base, 0.7);
  const tooltipFontSize = theme.typography.body2.fontSize ?? '0.875rem';
  const tooltipLineHeight = theme.typography.body2.lineHeight ?? 1.43;
  const tooltipFontWeight = theme.typography.fontWeightRegular ?? 400;
  const xLabels = data.map((item) => formatChartDateLabel(String(item.label)));
  const xTickLabelInterval = buildTickLabelInterval(xLabels.length);
  const seriesKeys = series.map((item) => item.key);
  const totalsByLabel = new Map<string, number>();
  data.forEach((row, index) => {
    const label = xLabels[index];
    if (!label) return;
    const total = seriesKeys.reduce((sum, key) => {
      const value = Number(row[key]);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0);
    totalsByLabel.set(label, total);
  });
  const topSeriesKeyByRow = data.map((row) => {
    let topKey: string | null = null;
    seriesKeys.forEach((key) => {
      const value = Number(row[key]);
      if (!Number.isFinite(value) || value === 0) return;
      topKey = key;
    });
    return topKey;
  });
  const resolveSeriesColor = (item: StackedBarSeries, index: number): string =>
    resolveBarColor(
      item.color ??
        (item.colorKey ? tokens.statusColors[item.colorKey] : palette[index % palette.length]),
    );
  const effectiveSkipAnimation = skipAnimation || suspendAnimationForResize;

  useEffect(() => {
    const element = containerRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;

    let previousWidth = element.clientWidth;
    let previousHeight = element.clientHeight;

    const observer = new ResizeObserver((entries) => {
      const [entry] = entries;
      const width = entry.contentRect.width;
      const heightValue = entry.contentRect.height;
      if (width === previousWidth && heightValue === previousHeight) return;
      previousWidth = width;
      previousHeight = heightValue;

      setSuspendAnimationForResize(true);
      if (resizeResetTimerRef.current !== null) {
        window.clearTimeout(resizeResetTimerRef.current);
      }
      resizeResetTimerRef.current = window.setTimeout(() => {
        setSuspendAnimationForResize(false);
        resizeResetTimerRef.current = null;
      }, 180);
    });

    observer.observe(element);
    return () => {
      observer.disconnect();
      if (resizeResetTimerRef.current !== null) {
        window.clearTimeout(resizeResetTimerRef.current);
        resizeResetTimerRef.current = null;
      }
    };
  }, []);

  const RoundedTopBar = (props: BarProps): JSX.Element => {
    const {
      ownerState,
      dataIndex,
      id,
      color,
      x,
      y,
      width,
      height: barHeight,
      layout,
      xOrigin,
      yOrigin,
      skipAnimation: skipAnimationProp,
      ...rest
    } = props;
    const animatedProps = useAnimateBar({
      x,
      y,
      width,
      height: barHeight,
      xOrigin,
      yOrigin,
      layout,
      skipAnimation: skipAnimationProp,
    });
    const animatedPathRef = animatedProps.ref as Ref<SVGPathElement>;
    const animatedX = animatedProps.x;
    const animatedY = animatedProps.y;
    const animatedWidth = animatedProps.width;
    const animatedHeight = animatedProps.height;
    const topKey = topSeriesKeyByRow[dataIndex];
    const isTop = typeof topKey === 'string' && topKey === String(id);
    const shouldRound = isTop && animatedHeight > 0;
    const filter = ownerState.isHighlighted ? 'brightness(120%)' : undefined;
    const opacity = ownerState.isFaded ? 0.3 : 1;
    const segmentRadius = Math.min(barRadius, animatedWidth / 2, animatedHeight / 2);
    const isSolo = isTop;
    const radius = Math.min(barRadius, animatedWidth / 2, animatedHeight);
    if (radius <= 0 || segmentRadius <= 0) {
      return (
        <rect
          {...rest}
          x={animatedX}
          y={animatedY}
          width={animatedWidth}
          height={animatedHeight}
          fill={color}
          stroke="none"
          filter={filter}
          opacity={opacity}
          ref={animatedProps.ref}
          data-highlighted={ownerState.isHighlighted ? true : undefined}
          data-faded={ownerState.isFaded ? true : undefined}
        />
      );
    }
    const right = animatedX + animatedWidth;
    const bottom = animatedY + animatedHeight;
    if (isSolo) {
      const roundedTopOnlyPath = [
        `M ${animatedX} ${bottom}`,
        `L ${animatedX} ${animatedY + radius}`,
        `Q ${animatedX} ${animatedY} ${animatedX + radius} ${animatedY}`,
        `L ${right - radius} ${animatedY}`,
        `Q ${right} ${animatedY} ${right} ${animatedY + radius}`,
        `L ${right} ${bottom}`,
        'Z',
      ].join(' ');
      return (
        <path
          {...rest}
          d={roundedTopOnlyPath}
          fill={color}
          stroke="none"
          filter={filter}
          opacity={opacity}
          ref={animatedPathRef}
          data-highlighted={ownerState.isHighlighted ? true : undefined}
          data-faded={ownerState.isFaded ? true : undefined}
        />
      );
    }
    if (!shouldRound) {
      return (
        <rect
          {...rest}
          x={animatedX}
          y={animatedY}
          width={animatedWidth}
          height={animatedHeight}
          fill={color}
          stroke="none"
          filter={filter}
          opacity={opacity}
          ref={animatedProps.ref}
          data-highlighted={ownerState.isHighlighted ? true : undefined}
          data-faded={ownerState.isFaded ? true : undefined}
        />
      );
    }
    const roundedPath = [
      `M ${animatedX} ${bottom}`,
      `L ${animatedX} ${animatedY + radius}`,
      `Q ${animatedX} ${animatedY} ${animatedX + radius} ${animatedY}`,
      `L ${right - radius} ${animatedY}`,
      `Q ${right} ${animatedY} ${right} ${animatedY + radius}`,
      `L ${right} ${bottom}`,
      'Z',
    ].join(' ');
    return (
      <path
        {...rest}
        d={roundedPath}
        fill={color}
        stroke="none"
        filter={filter}
        opacity={opacity}
        ref={animatedPathRef}
        data-highlighted={ownerState.isHighlighted ? true : undefined}
        data-faded={ownerState.isFaded ? true : undefined}
      />
    );
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height,
        color: theme.app.colors.divider,
        '& .MuiChartsSurface-root': { color: theme.app.colors.divider },
      }}
    >
      <GlobalStyles
        styles={{
          '.flowqa-stacked-tooltip-paper': {
            borderRadius: r('md'),
            border: `1px solid ${theme.app.colors.border}`,
            boxShadow: 'none',
            backgroundColor: theme.app.colors.surface,
            color: theme.app.colors.text,
            paddingTop: 8,
            paddingBottom: 6,
            paddingLeft: 4,
            paddingRight: 4,
            fontSize: theme.typography.body2.fontSize,
            lineHeight: theme.typography.body2.lineHeight,
            fontWeight: theme.typography.fontWeightRegular,
          },
          '.flowqa-stacked-tooltip-paper *': {
            fontSize: theme.typography.body2.fontSize,
            lineHeight: theme.typography.body2.lineHeight,
            fontWeight: theme.typography.fontWeightRegular,
          },
          '.flowqa-stacked-tooltip-table': {
            borderSpacing: 0,
            border: '0 !important',
            width: 'auto !important',
            tableLayout: 'auto',
          },
          '.flowqa-stacked-tooltip-table > thead > tr > th, .flowqa-stacked-tooltip-table > thead > tr > td':
            {
              borderBottom: `1px solid ${theme.app.colors.border} !important`,
              paddingTop: '0px',
              paddingBottom: '4px',
              paddingLeft: '4px !important',
              paddingRight: '4px !important',
              lineHeight: 1.05,
              textAlign: 'left',
              verticalAlign: 'middle !important',
            },
          '.flowqa-stacked-tooltip-table > thead > tr > th': {
            fontWeight: theme.typography.fontWeightRegular,
          },
          '.flowqa-stacked-tooltip-table > thead > tr > td.flowqa-stacked-tooltip-header-total-label':
            {
              textAlign: 'right !important',
              whiteSpace: 'nowrap',
            },
          '.flowqa-stacked-tooltip-table > thead > tr > td.flowqa-stacked-tooltip-header-total-value':
            {
              textAlign: 'right !important',
              whiteSpace: 'nowrap',
              fontVariantNumeric: 'tabular-nums',
            },
          '.flowqa-stacked-tooltip-table.flowqa-stacked-tooltip-table-empty > thead > tr > th, .flowqa-stacked-tooltip-table.flowqa-stacked-tooltip-table-empty > thead > tr > td':
            {
              borderBottom: '0 !important',
              paddingBottom: '0 !important',
            },
          '.flowqa-stacked-tooltip-table > tbody > tr > th, .flowqa-stacked-tooltip-table > tbody > tr > td':
            {
              border: '0 !important',
              paddingLeft: '6px !important',
              paddingRight: '6px !important',
              verticalAlign: 'middle !important',
            },
          '.flowqa-stacked-tooltip-table > tbody > tr.flowqa-stacked-tooltip-total-row > th, .flowqa-stacked-tooltip-table > tbody > tr.flowqa-stacked-tooltip-total-row > td':
            {
              paddingTop: '6px !important',
              textAlign: 'left !important',
            },
          '.flowqa-stacked-tooltip-table > tbody > tr:first-of-type > th, .flowqa-stacked-tooltip-table > tbody > tr:first-of-type > td':
            {
              paddingTop: '8px !important',
            },
          '.flowqa-stacked-tooltip-table > tbody > tr:last-of-type > th, .flowqa-stacked-tooltip-table > tbody > tr:last-of-type > td':
            {
              paddingBottom: '4px !important',
            },
          '.flowqa-stacked-tooltip-table .MuiChartsTooltip-markContainer': {
            width: '20px',
            display: 'inline-flex',
            alignItems: 'center',
            verticalAlign: 'middle',
          },
          '.flowqa-stacked-tooltip-cell': {
            paddingTop: '2px',
            paddingBottom: '2px',
            lineHeight: 1.1,
          },
          '.flowqa-stacked-tooltip-count-cell': {
            textAlign: 'right !important',
            whiteSpace: 'nowrap',
            fontVariantNumeric: 'tabular-nums',
          },
          '.flowqa-stacked-tooltip-percent-cell': {
            textAlign: 'right !important',
            whiteSpace: 'nowrap',
            minWidth: '40px',
            fontVariantNumeric: 'tabular-nums',
          },
          '.flowqa-stacked-tooltip-mark': {
            marginTop: 0,
            marginBottom: 0,
          },
          '.flowqa-stacked-tooltip-paper.flowqa-stacked-tooltip-paper-empty': {
            paddingBottom: 4,
          },
        }}
      />
      <MuiBarChart
        height={height}
        hideLegend
        skipAnimation={effectiveSkipAnimation}
        borderRadius={barRadius}
        slots={{
          bar: RoundedTopBar,
          tooltip: (tooltipProps) => (
            <StackedTotalsTooltip {...tooltipProps} totalsByLabel={totalsByLabel} />
          ),
        }}
        onItemClick={onSelectBar && !disableInteraction ? handleItemClick : undefined}
        onAxisClick={onSelectBar && !disableInteraction ? handleAxisClick : undefined}
        xAxis={[
          {
            data: xLabels,
            scaleType: 'band',
            categoryGapRatio,
            barGapRatio,
            valueFormatter: (value) => formatChartDateLabel(String(value)),
            tickLabelInterval: xTickLabelInterval,
            tickLabelStyle: { fill: theme.app.colors.textMuted, transform: 'translateY(4px)' },
            tickSize: 6,
          },
        ]}
        yAxis={[
          {
            min: 0,
            tickLabelStyle: { fill: theme.app.colors.textMuted },
            tickSize: 0,
            valueFormatter: formatWholeNumber,
          },
        ]}
        grid={{ horizontal: true, vertical: false }}
        margin={{ left: -8, right: 16, top: 16, bottom: 14 }}
        slotProps={{
          axisLine: { stroke: theme.app.colors.divider },
          axisTick: { stroke: theme.app.colors.divider },
          tooltip: {
            classes: {
              paper: 'flowqa-stacked-tooltip-paper',
              table: 'flowqa-stacked-tooltip-table',
              cell: 'flowqa-stacked-tooltip-cell',
              mark: 'flowqa-stacked-tooltip-mark',
            },
            sx: {
              fontSize: tooltipFontSize,
              lineHeight: tooltipLineHeight,
              fontWeight: tooltipFontWeight,
              paddingTop: '0 !important',
              paddingBottom: '0 !important',
              paddingLeft: '6px !important',
              paddingRight: '6px !important',
              '& .MuiChartsTooltip-table > thead > tr > th, & .MuiChartsTooltip-table > thead > tr > td, & table > thead > tr > th, & table > thead > tr > td':
                {
                  borderBottom: `1px solid ${theme.app.colors.border} !important`,
                  boxShadow: 'none !important',
                  paddingLeft: '4px !important',
                  paddingRight: '4px !important',
                  paddingTop: '0 !important',
                  paddingBottom: '4px !important',
                  textAlign: 'left',
                  verticalAlign: 'middle !important',
                },
              '& .MuiChartsTooltip-table.flowqa-stacked-tooltip-table-empty > thead > tr > th, & .MuiChartsTooltip-table.flowqa-stacked-tooltip-table-empty > thead > tr > td, & table.flowqa-stacked-tooltip-table-empty > thead > tr > th, & table.flowqa-stacked-tooltip-table-empty > thead > tr > td':
                {
                  borderBottom: '0 !important',
                  paddingBottom: '0 !important',
                },
              '& .MuiChartsTooltip-table, & table': {
                borderCollapse: 'separate',
                borderSpacing: 0,
                width: 'auto !important',
                tableLayout: 'auto',
              },
              '& .MuiChartsTooltip-markContainer': {
                width: '20px !important',
                display: 'inline-flex !important',
                alignItems: 'center !important',
                verticalAlign: 'middle !important',
              },
              '& .MuiChartsTooltip-row, & tr': {
                lineHeight: '1.1 !important',
              },
              '& .MuiTypography-root, & .MuiChartsTooltip-cell, & .MuiChartsTooltip-labelCell, & .MuiChartsTooltip-valueCell, & .MuiChartsTooltip-axisValueCell, & caption, & td, & th':
                {
                  color: theme.app.colors.text,
                  fontSize: tooltipFontSize,
                  lineHeight: tooltipLineHeight,
                  fontWeight: tooltipFontWeight,
                  textAlign: 'left !important',
                },
              '& .MuiChartsTooltip-cell, & .MuiChartsTooltip-markCell, & .MuiChartsTooltip-labelCell, & .MuiChartsTooltip-valueCell':
                {
                  paddingTop: '2px !important',
                  paddingBottom: '2px !important',
                  lineHeight: '1.1 !important',
                  verticalAlign: 'middle !important',
                },
              '& .MuiChartsTooltip-table > tbody > tr.flowqa-stacked-tooltip-total-row > th, & .MuiChartsTooltip-table > tbody > tr.flowqa-stacked-tooltip-total-row > td':
                {
                  paddingTop: '6px !important',
                  textAlign: 'left !important',
                },
              '& .MuiChartsTooltip-table > tbody > tr:first-of-type > th, & .MuiChartsTooltip-table > tbody > tr:first-of-type > td':
                {
                  paddingTop: '8px !important',
                },
              '& .MuiChartsTooltip-table > tbody > tr:last-of-type > th, & .MuiChartsTooltip-table > tbody > tr:last-of-type > td':
                {
                  paddingBottom: '4px !important',
                },
              '& .flowqa-stacked-tooltip-count-cell, & .flowqa-stacked-tooltip-percent-cell': {
                textAlign: 'right !important',
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
              },
              '& .flowqa-stacked-tooltip-percent-cell': {
                minWidth: '40px',
              },
              '& .MuiChartsTooltip-mark': {
                marginTop: '0 !important',
                marginBottom: '0 !important',
              },
              '& .flowqa-stacked-tooltip-header-total-label': {
                textAlign: 'right !important',
                whiteSpace: 'nowrap',
              },
              '& .flowqa-stacked-tooltip-header-total-value': {
                textAlign: 'right !important',
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              },
              '& .MuiChartsTooltip-axisValue, & .MuiChartsTooltip-axisValueCell': {
                marginTop: '0 !important',
                marginBottom: '0 !important',
                paddingTop: '0 !important',
                paddingBottom: '0 !important',
                lineHeight: '1.05 !important',
              },
            },
          },
        }}
        sx={{
          '& .MuiChartsAxis-tickLabel': { fill: theme.app.colors.textMuted },
          '& .MuiChartsAxis-directionY .MuiChartsAxis-tickLabel': {
            transform: 'translateX(-6px)',
          },
          '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
            stroke: theme.app.colors.divider,
            strokeOpacity: 1,
          },
          '& .MuiChartsGrid-horizontalLine': { stroke: theme.app.colors.divider },
          '& .MuiChartsAxis-line': {
            stroke: theme.app.colors.divider,
            strokeOpacity: 1,
          },
          '& .MuiChartsAxis-line[stroke]': {
            stroke: `${theme.app.colors.divider} !important`,
          },
          '& .MuiChartsAxis-tick[stroke]': {
            stroke: `${theme.app.colors.divider} !important`,
          },
          '& .MuiChartsAxis-directionX .MuiChartsAxis-line, & .MuiChartsAxis-directionX .MuiChartsAxis-line[stroke]':
            {
              stroke: 'transparent !important',
            },
          ...(onSelectBar && !disableInteraction
            ? {
                '& .MuiChartsAxis-directionX': { cursor: 'pointer' },
                '& .MuiChartsAxis-directionX .MuiChartsAxis-tickLabel': { cursor: 'pointer' },
                '& .MuiChartsAxis-directionX .MuiChartsAxis-tick': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-root': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-root *': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight *': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-line, & .MuiChartsAxisHighlight-rect': {
                  cursor: 'pointer',
                },
                '& .MuiChartsAxisHighlightLine, & .MuiChartsAxisHighlightRect': {
                  cursor: 'pointer',
                },
                '& .MuiChartsOverlay-root': { cursor: 'pointer' },
                '& svg': { cursor: 'pointer' },
              }
            : null),
          ...(disableInteraction
            ? {
                '& .MuiChartsOverlay-root': { pointerEvents: 'none' },
                '& .MuiChartsAxisHighlight-root': { pointerEvents: 'none' },
                '& .MuiChartsAxisHighlight': { pointerEvents: 'none' },
                '& .MuiBarElement-root': { pointerEvents: 'none' },
              }
            : null),
          ...(disableTooltip
            ? {
                '& .MuiChartsTooltip-root': { display: 'none' },
                '& .MuiChartsTooltip-paper': { display: 'none' },
              }
            : null),
        }}
        series={series.map((item, index) => ({
          id: item.key,
          data: data.map((row) => Number(row[item.key])),
          label: item.label,
          color: resolveSeriesColor(item, index),
          stack: stackId,
          valueFormatter: (value) => formatTooltipValue(value),
        }))}
      />
    </Box>
  );
};

export const StackedBarChart = memo(StackedBarChartBase);
